
<!-- src/views/ForecastView.vue -->
<template>
  <div class="container forecast-view">
    <h4>Forecast</h4>

    <div
      v-if="error"
      class="card-panel red lighten-5 red-text text-darken-4"
    >
      {{ error }}
    </div>

    <div
      v-if="aiError"
      class="card-panel amber lighten-5 brown-text text-darken-3"
    >
      {{ aiError }}
    </div>

    <div
      v-if="loading || aiLoading"
      class="forecast-loading"
      role="status"
      aria-label="Loading forecast"
    >
      <div class="progress forecast-progress">
        <div class="determinate forecast-progress-fill"></div>
      </div>

      <p class="grey-text">
        {{
          aiLoading
            ? "Preparing forecasts from trading history..."
            : "Loading forecasts and Audit trading patterns..."
        }}
      </p>
    </div>

    <div
      v-if="!venueId"
      class="card-panel amber lighten-5"
    >
      Select a venue to view its forecasts.
    </div>

    <div class="forecast-week-list">
      <ForecastWeekInput
        v-for="wk in weeks"
        :key="`${venueId}-${wk.weekEndingDateISO}`"
        :week="wk"
        :categories="CATEGORIES"
        :days="DAYS"
        :formatUK="formatUK"
        :editable="
          roleLoaded &&
          isEditable(wk.weekEndingDateISO)
        "
        :submitted="isSubmitted(wk.weekEndingDateISO)"
        :canSubmit="
          roleLoaded &&
          canSubmit(wk.weekEndingDateISO)
        "
        :canAdjust="
          roleLoaded &&
          canAdjust(wk.weekEndingDateISO)
        "
        :busy="loading || aiLoading"
        :lockMessage="
          lockMessageForWeek(wk.weekEndingDateISO)
        "
        @cell="
          ({ category, dayIndex, value }) =>
            setCell(
              wk.weekEndingDateISO,
              category,
              dayIndex,
              value
            )
        "
        @submit="
          () => submitWeek(wk.weekEndingDateISO)
        "
        @adjust="
          () => enableAdjust(wk.weekEndingDateISO)
        "
        @stop-adjust="
          () => disableAdjust(wk.weekEndingDateISO)
        "
        @regenerate="
          () => handleRegenerate(wk.weekEndingDateISO)
        "
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
} from "vue";

import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/firebase";

import ForecastWeekInput from "@/components/ForecastWeekInput.vue";
import { useForecasts } from "@/scripts/useScripts/useForecasts";
import { useUserRole } from "@/scripts/useScripts/useUserRole";

function cleanVenueId(value) {
  return String(value || "").trim();
}

function getStoredVenueId() {
  return cleanVenueId(
    localStorage.getItem("venue")
  );
}

const venueId = ref(getStoredVenueId());

const userRef = ref({
  uid: null,
  email: null,
});

const {
  isAdmin,
  roleLoaded,
  loadUserRole,
} = useUserRole();

const isAdminRef = isAdmin;

const {
  CATEGORIES,
  DAYS,
  loading,
  error,
  weeks,
  formatUK,
  aiLoading,
  aiError,
  isInCurrentPlus2Window,
  isSubmitted,
  canSubmit,
  canAdjust,
  isEditable,
  refreshAll,
  autoPopulateForecasts,
  regenerateWeek,
  setCell,
  submitWeek,
  enableAdjust,
  disableAdjust,
} = useForecasts({
  venueIdRef: venueId,
  isAdminRef,
  userRef,
});

// ==========================
// Refresh / venue switching
// ==========================

let refreshToken = 0;

async function refreshForecastsForVenue() {
  const token = ++refreshToken;
  const requestedVenue = venueId.value;

  if (
    !requestedVenue ||
    !roleLoaded.value ||
    !userRef.value.uid
  ) return;

  await refreshAll();

  if (
    token !== refreshToken ||
    requestedVenue !== venueId.value ||
    !roleLoaded.value
  ) return;

  await autoPopulateForecasts();
}

watch(
  [venueId, roleLoaded, () => userRef.value.uid],
  async ([newVenue, loaded, uid]) => {
    if (!newVenue || !loaded || !uid) return;
    await refreshForecastsForVenue();
  },
  { immediate: true }
);

// ==========================
// AI regeneration
// ==========================

async function handleRegenerate(weekISO) {
  if (
    !roleLoaded.value ||
    !isEditable(weekISO) ||
    loading.value ||
    aiLoading.value
  ) return;

  const message = isSubmitted(weekISO)
    ? (
      "This submitted forecast is currently in " +
      "authorised adjustment mode.\n\n" +
      "Replace its figures using the latest trading " +
      "history and Audit closed-day settings?"
    )
    : (
      "Regenerate this forecast using the latest " +
      "trading history and Audit closed-day settings?\n\n" +
      "This will replace existing figures, including " +
      "manual changes."
    );

  if (!window.confirm(message)) return;

  try {
    await regenerateWeek(weekISO);
  } catch (e) {
    console.error(
      "[Forecast] Regeneration failed:",
      e
    );
  }
}

// ==========================
// Keep selected venue synced
// ==========================

function syncVenueFromStorage() {
  const nextVenueId = getStoredVenueId();

  if (nextVenueId !== venueId.value) {
    venueId.value = nextVenueId;
  }
}

function handleStorageEvent(event) {
  if (event.key === "venue") {
    syncVenueFromStorage();
  }
}

function handleVenueChangedEvent(event) {
  const detail = event?.detail || {};

  const nextVenueId = cleanVenueId(
    detail.venueId ||
    detail.id ||
    detail.venue ||
    detail.value ||
    localStorage.getItem("venue")
  );

  if (nextVenueId && nextVenueId !== venueId.value) {
    localStorage.setItem("venue", nextVenueId);
    venueId.value = nextVenueId;
  } else {
    syncVenueFromStorage();
  }
}

// ==========================
// Authentication / role
// ==========================

let venuePollTimer = null;
let stopAuthListener = null;

onMounted(() => {
  stopAuthListener = onAuthStateChanged(
    auth,
    async (user) => {
      if (user) {
        userRef.value = {
          uid: user.uid,
          email: user.email || null,
        };

        await loadUserRole(user.uid);
      } else {
        userRef.value = {
          uid: null,
          email: null,
        };

        await loadUserRole(null);
      }

      syncVenueFromStorage();
    }
  );

  window.addEventListener(
    "storage",
    handleStorageEvent
  );

  window.addEventListener(
    "venue-changed",
    handleVenueChangedEvent
  );

  window.addEventListener(
    "coredeck:venue-changed",
    handleVenueChangedEvent
  );

  window.addEventListener(
    "focus",
    syncVenueFromStorage
  );

  venuePollTimer = window.setInterval(
    syncVenueFromStorage,
    500
  );
});

onUnmounted(() => {
  ++refreshToken;

  if (typeof stopAuthListener === "function") {
    stopAuthListener();
  }

  window.removeEventListener(
    "storage",
    handleStorageEvent
  );

  window.removeEventListener(
    "venue-changed",
    handleVenueChangedEvent
  );

  window.removeEventListener(
    "coredeck:venue-changed",
    handleVenueChangedEvent
  );

  window.removeEventListener(
    "focus",
    syncVenueFromStorage
  );

  if (venuePollTimer) {
    window.clearInterval(venuePollTimer);
    venuePollTimer = null;
  }
});

// ==========================
// Existing lock messaging
// ==========================

function lockMessageForWeek(weekISO) {
  if (!roleLoaded.value) {
    return "Loading permissions…";
  }

  if (
    !isAdminRef.value &&
    isInCurrentPlus2Window(weekISO)
  ) {
    return (
      "This week is within the current week ending " +
      "+2 window. Forecast is locked for non-admin users."
    );
  }

  if (
    isSubmitted(weekISO) &&
    !isEditable(weekISO)
  ) {
    return (
      "This week has been submitted. " +
      "Click 'Adjust forecast' to make changes " +
      "(if allowed)."
    );
  }

  return "";
}
</script>

<style scoped>
.forecast-view {
  margin-top: 18px;
}

.forecast-loading {
  margin: 12px 0 18px;
}

.forecast-progress.progress {
  height: 8px;
  margin: 0;
  border-radius: 999px;
  overflow: hidden;
  background: #c8e6c9;
  box-shadow: none;
}

.forecast-progress .forecast-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: #388e3c;
  animation: forecast-progress-fill 1.35s
    ease-in-out infinite;
}

@keyframes forecast-progress-fill {
  0% {
    width: 0%;
    opacity: 0.75;
  }

  75% {
    width: 100%;
    opacity: 1;
  }

  100% {
    width: 100%;
    opacity: 0;
  }
}

.forecast-week-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

@media (max-width: 600px) {
  .forecast-view {
    width: 96%;
    margin-top: 12px;
  }

  .forecast-view h4 {
    font-size: 1.8rem;
    margin-top: 0;
  }

  .forecast-loading {
    margin: 10px 0 14px;
  }

  .forecast-week-list {
    gap: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .forecast-view :deep(table) {
    min-width: 680px;
  }

  .forecast-view :deep(.card-content) {
    padding: 14px;
  }
}
</style>
